/**
 * The search page — /newsstudios/search/?q=…  (Newsstudios/search.ctp)
 *
 * The controller renders the form; the hits come from a JSON POST to
 * `newsstudios/searchajax`, the same endpoint base_neo and faradeed_neo drive.
 *
 * THE URL IS THE STATE. The filter form is a plain GET form that submits back to
 * this page, so what to search for is read from the query string on load and
 * pagination is real <a href="?…&page=N"> links. A result set can be shared,
 * reloaded and walked with the back button; nothing is intercepted.
 *
 * NO HTML STRINGS. Rows and pagination are cloned from <template>s in the view
 * and filled with textContent / setAttribute. Every URL that came from the API
 * goes through getSafeUrl() — an http(s) allow-list resolved against our own
 * origin, the same helper the fleet's search.ctp migration uses.
 */
import { qs } from './dom.js';

const LIMIT = 10;
// Meilisearch's default maxTotalHits is 1000: past page 100 there is nothing.
const MAX_PAGES = 100;
// Pages either side of the current one — structure/lists/pagination.ctp's default.
const WINDOW = 2;

function getSafeUrl(url) {
  if (!url) return '#';
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch {
    return '#';
  }
  return '#';
}

// Core_Text::persianFixNumber, for the digits: the server-rendered pager shows
// Persian-Indic numerals and these rows sit on the same site. Editor-typed text
// (title, lead, upTitle, section name) is NOT passed through it — it shows the
// digits the editor typed, same as the server-rendered rows.
const isFa = document.documentElement.lang === 'fa';
const digits = (value) =>
  isFa ? String(value).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]) : String(value);

// A positive integer or nothing. The form sends one id per field; the endpoint
// wants an array, and casts again server-side.
function ids(value) {
  const id = parseInt(value, 10);
  return id > 0 ? [id] : [];
}

// "1404-07-08" from the three selects date-select.ctp renders. The server
// pre-selects them from the query string, so they ARE the submitted value.
function dateFrom(field) {
  const part = (name) => qs(`#date-${field}-${name}`)?.value;
  const [y, m, d] = [part('year'), part('month'), part('day')];
  return y && m && d ? `${y}-${m}-${d}` : '';
}

function clone(root, name) {
  return qs(`template[data-search-${name}]`, root).content.firstElementChild.cloneNode(true);
}

function renderRow(root, item) {
  const row = clone(root, 'row');
  const link = qs('[data-row-link]', row);
  link.href = getSafeUrl(item.url);
  link.title = item.title || '';

  const picture = item.primaryFile && item.primaryFile.picture;
  if (picture) qs('[data-row-image]', row).src = getSafeUrl(picture);

  // category-pill.ctp: the section name, else the kicker, else no pill at all.
  const pill = qs('[data-row-pill]', row);
  const pillLabel = (item.categories && item.categories.title) || item.upTitle || '';
  if (pillLabel) pill.textContent = pillLabel;
  else pill.remove();

  qs('[data-row-title]', row).textContent = item.title || '';

  const lead = qs('[data-row-lead]', row);
  if (item.lead) lead.textContent = item.lead;
  else lead.remove();

  const time = qs('[data-row-time]', row);
  if (item.publish_time) time.textContent = item.publish_time;
  else time.remove();

  return row;
}

function pageHref(page) {
  const params = new URLSearchParams(window.location.search);
  if (page > 1) params.set('page', String(page));
  else params.delete('page');
  const query = params.toString();
  return window.location.pathname + (query ? `?${query}` : '');
}

// The same first / last / window-around-current shape as pagination.ctp.
function renderPagination(root, nav, page, pages) {
  const bar = qs('.page-nav', nav);
  bar.textContent = '';
  if (pages < 2) return;

  const add = (el, target) => {
    if (target) el.href = pageHref(target);
    bar.appendChild(el);
  };

  if (page > 1) add(clone(root, 'page-prev'), page - 1);

  let printed = 0;
  for (let n = 1; n <= pages; n += 1) {
    if (n !== 1 && n !== pages && Math.abs(n - page) > WINDOW) continue;
    if (printed > 0 && n > printed + 1) add(clone(root, 'page-gap'));

    if (n === page) {
      const current = clone(root, 'page-current');
      current.textContent = digits(n);
      add(current);
    } else {
      const link = clone(root, 'page-link');
      link.textContent = digits(n);
      link.title = `${link.title} ${digits(n)}`;
      add(link, n);
    }
    printed = n;
  }

  if (page < pages) add(clone(root, 'page-next'), page + 1);
  nav.hidden = false;
}

export function setupSearchPage() {
  const root = qs('[data-search-page]');
  if (!root) return;

  const status = qs('[data-search-status]', root);
  const list = qs('[data-search-results]', root);
  const nav = qs('[data-search-pagination]', root);
  const params = new URLSearchParams(window.location.search);

  const page = Math.min(Math.max(parseInt(params.get('page'), 10) || 1, 1), MAX_PAGES);

  // search-filters.ctp sends matches=0 and, when ticked, matches=1 after it.
  // Absent (the masthead and drawer boxes) means exact phrase, as before.
  const matches = params.getAll('matches');

  const body = {
    matches: !matches.length || matches.includes('1'),
    query: (params.get('q') || '').trim(),
    categories: ids(params.get('categories')),
    types: ids(params.get('types')),
    dateStart: dateFrom('start'),
    dateEnd: dateFrom('end'),
    offset: (page - 1) * LIMIT,
    limit: LIMIT,
    props: {
      // Formatted server-side, in the site's calendar — archive-list.ctp's format.
      dateFormat: 'dd MMMM yyyy',
      primaryFile: {
        w: parseInt(root.dataset.searchThumbW, 10),
        h: parseInt(root.dataset.searchThumbH, 10),
        zc: true,
      },
    },
  };

  const say = (key) => {
    status.textContent = status.dataset[key] || '';
    status.hidden = false;
  };

  fetch(root.dataset.searchEndpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`searchajax answered ${response.status}`);
      return response.json();
    })
    .then((data) => {
      // newsList() can come back keyed by id rather than as a list.
      const items = Array.isArray(data.result) ? data.result : Object.values(data.result || {});
      if (!items.length) {
        say('textEmpty');
        // An exact-phrase miss: offer the same search, word by word.
        const loosen = qs('[data-search-loosen]', root);
        if (loosen && body.matches && body.query) {
          const loose = new URLSearchParams(window.location.search);
          loose.set('matches', '0');
          loose.delete('page');
          qs('a', loosen).href = `${window.location.pathname}?${loose}`;
          loosen.hidden = false;
        }
        return;
      }

      list.textContent = '';
      for (const item of items) list.appendChild(renderRow(root, item));
      list.hidden = false;
      status.hidden = true;

      const total = parseInt(data.estimatedTotalHits, 10) || items.length;
      renderPagination(root, nav, page, Math.min(Math.ceil(total / LIMIT), MAX_PAGES));
    })
    .catch((error) => {
      say('textError');
      console.error('[search-page]', error);
    });
}
